// PWA Utilities and Service Worker Registration
class PWAManager {
  constructor() {
    this.deferredPrompt = null;
    this.isInstalled = false;
    this.isOnline = navigator.onLine;
    this.registration = null;
    
    this.init();
  }
  
  async init() {
    try {
      // Register service worker
      await this.registerServiceWorker();
      
      // Setup PWA event listeners
      this.setupEventListeners();
      
      // Check if app is installed
      this.checkInstallationStatus();
      
      // Initialize background sync
      this.initializeBackgroundSync();
      
      console.log('PWA Manager initialized successfully');
    } catch (error) {
      console.error('PWA Manager initialization failed:', error);
    }
  }
  
  async registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        this.registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        
        console.log('Service Worker registered successfully:', this.registration);
        
        // Listen for updates
        this.registration.addEventListener('updatefound', () => {
          const newWorker = this.registration.installing;
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available
              this.showUpdateAvailable();
            }
          });
        });
        
        // Force update check every 30 minutes
        setInterval(() => {
          this.registration.update();
        }, 30 * 60 * 1000);
        
      } catch (error) {
        console.error('Service Worker registration failed:', error);
        throw error;
      }
    } else {
      console.warn('Service Workers are not supported in this browser');
    }
  }
  
  setupEventListeners() {
    // Install prompt handling
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.showInstallButton();
    });
    
    // App installed event
    window.addEventListener('appinstalled', () => {
      this.isInstalled = true;
      this.hideInstallButton();
      this.trackEvent('pwa_installed');
      console.log('PWA was installed');
    });
    
    // Online/offline status
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.handleOnlineStatus();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.handleOfflineStatus();
    });
    
    // Visibility change for background sync
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isOnline) {
        this.syncWhenVisible();
      }
    });
    
    // Unhandled promise rejections (for offline scenarios)
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason?.name === 'TypeError' && !this.isOnline) {
        event.preventDefault();
        this.handleOfflineError(event.reason);
      }
    });
  }
  
  checkInstallationStatus() {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true) {
      this.isInstalled = true;
      console.log('App is running in standalone mode');
    }
    
    // Check for related app
    if ('getInstalledRelatedApps' in navigator) {
      navigator.getInstalledRelatedApps().then(apps => {
        if (apps.length > 0) {
          this.isInstalled = true;
          console.log('Related app is installed:', apps);
        }
      });
    }
  }
  
  async installApp() {
    if (!this.deferredPrompt) {
      console.log('No install prompt available');
      return false;
    }
    
    try {
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        this.trackEvent('pwa_install_accepted');
      } else {
        console.log('User dismissed the install prompt');
        this.trackEvent('pwa_install_dismissed');
      }
      
      this.deferredPrompt = null;
      return outcome === 'accepted';
    } catch (error) {
      console.error('Error during app installation:', error);
      return false;
    }
  }
  
  showInstallButton() {
    // Create and show install button
    let installButton = document.getElementById('pwa-install-button');
    
    if (!installButton) {
      installButton = document.createElement('button');
      installButton.id = 'pwa-install-button';
      installButton.innerHTML = `
        <span>📱</span>
        <span>Install App</span>
      `;
      installButton.className = 'pwa-install-btn';
      installButton.onclick = () => this.installApp();
      
      // Add to appropriate container
      const container = document.querySelector('.navbar') || 
                       document.querySelector('header') || 
                       document.body;
      container.appendChild(installButton);
    }
    
    installButton.style.display = 'flex';
    
    // Auto-hide after 10 seconds
    setTimeout(() => {
      if (installButton && !this.isInstalled) {
        installButton.style.opacity = '0.7';
      }
    }, 10000);
  }
  
  hideInstallButton() {
    const installButton = document.getElementById('pwa-install-button');
    if (installButton) {
      installButton.style.display = 'none';
    }
  }
  
  showUpdateAvailable() {
    const updateBanner = document.createElement('div');
    updateBanner.id = 'pwa-update-banner';
    updateBanner.innerHTML = `
      <div class="update-content">
        <span>🔄 New version available!</span>
        <button onclick="window.pwaManager.updateApp()">Update</button>
        <button onclick="this.parentElement.parentElement.remove()">Later</button>
      </div>
    `;
    updateBanner.className = 'pwa-update-banner';
    
    document.body.appendChild(updateBanner);
    
    this.trackEvent('pwa_update_available');
  }
  
  updateApp() {
    if (this.registration && this.registration.waiting) {
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }
  
  handleOnlineStatus() {
    console.log('App is online');
    
    // Remove offline indicators
    document.querySelectorAll('.offline-indicator').forEach(el => {
      el.style.display = 'none';
    });
    
    // Sync pending data
    this.syncPendingData();
    
    // Show online notification
    this.showNotification('Connection restored', 'You\'re back online!', 'success');
    
    this.trackEvent('pwa_online');
  }
  
  handleOfflineStatus() {
    console.log('App is offline');
    
    // Show offline indicator
    this.showOfflineIndicator();
    
    // Show offline notification
    this.showNotification('You\'re offline', 'Some features may be limited', 'warning');
    
    this.trackEvent('pwa_offline');
  }
  
  showOfflineIndicator() {
    let offlineIndicator = document.getElementById('offline-indicator');
    
    if (!offlineIndicator) {
      offlineIndicator = document.createElement('div');
      offlineIndicator.id = 'offline-indicator';
      offlineIndicator.className = 'offline-indicator';
      offlineIndicator.innerHTML = `
        <span>📡</span>
        <span>Offline Mode</span>
      `;
      
      document.body.appendChild(offlineIndicator);
    }
    
    offlineIndicator.style.display = 'flex';
  }
  
  initializeBackgroundSync() {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      console.log('Background sync is supported');
      
      // Register sync events
      this.registerSyncEvents();
    } else {
      console.log('Background sync is not supported');
    }
  }
  
  async registerSyncEvents() {
    if (this.registration) {
      try {
        await this.registration.sync.register('contact-form-sync');
        console.log('Background sync registered for contact forms');
      } catch (error) {
        console.error('Background sync registration failed:', error);
      }
    }
  }
  
  async syncPendingData() {
    try {
      // Trigger manual sync for pending data
      if (this.registration && this.registration.sync) {
        await this.registration.sync.register('manual-sync');
      }
      
      // Also handle any pending forms in memory
      this.processPendingForms();
    } catch (error) {
      console.error('Manual sync failed:', error);
    }
  }
  
  async processPendingForms() {
    const pendingForms = this.getPendingForms();
    
    for (const form of pendingForms) {
      try {
        await this.submitForm(form);
        this.removePendingForm(form.id);
      } catch (error) {
        console.error('Failed to submit pending form:', error);
      }
    }
  }
  
  async submitForm(formData) {
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }
  
  addPendingForm(formData) {
    const pendingForms = this.getPendingForms();
    pendingForms.push({
      id: Date.now(),
      data: formData,
      timestamp: Date.now()
    });
    
    localStorage.setItem('pendingForms', JSON.stringify(pendingForms));
  }
  
  getPendingForms() {
    try {
      return JSON.parse(localStorage.getItem('pendingForms') || '[]');
    } catch {
      return [];
    }
  }
  
  removePendingForm(id) {
    const pendingForms = this.getPendingForms().filter(form => form.id !== id);
    localStorage.setItem('pendingForms', JSON.stringify(pendingForms));
  }
  
  syncWhenVisible() {
    if (this.isOnline && !document.hidden) {
      this.syncPendingData();
    }
  }
  
  handleOfflineError(error) {
    console.log('Handling offline error:', error);
    
    // Show user-friendly offline message
    this.showNotification(
      'Action saved',
      'Your action will be completed when you\'re back online',
      'info'
    );
  }
  
  showNotification(title, message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `pwa-notification ${type}`;
    notification.innerHTML = `
      <div class="notification-content">
        <strong>${title}</strong>
        <p>${message}</p>
      </div>
      <button onclick="this.parentElement.remove()">×</button>
    `;
    
    document.body.appendChild(notification);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 5000);
  }
  
  trackEvent(eventName, data = {}) {
    try {
      // Track PWA events for analytics
      if (window.gtag) {
        window.gtag('event', eventName, {
          event_category: 'PWA',
          event_label: 'Makrana Marble PWA',
          ...data
        });
      }
      
      // Also store locally for offline analysis
      const events = JSON.parse(localStorage.getItem('pwa_events') || '[]');
      events.push({
        event: eventName,
        data,
        timestamp: Date.now()
      });
      
      // Keep only last 100 events
      if (events.length > 100) {
        events.splice(0, events.length - 100);
      }
      
      localStorage.setItem('pwa_events', JSON.stringify(events));
    } catch (error) {
      console.error('Error tracking PWA event:', error);
    }
  }
  
  getInstallationMetrics() {
    return {
      isInstalled: this.isInstalled,
      isOnline: this.isOnline,
      hasServiceWorker: !!this.registration,
      hasInstallPrompt: !!this.deferredPrompt,
      pendingForms: this.getPendingForms().length,
      events: JSON.parse(localStorage.getItem('pwa_events') || '[]').length
    };
  }
}

// CSS for PWA components
const pwaStyles = `
  .pwa-install-btn {
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: linear-gradient(135deg, #8B4513 0%, #D2691E 100%);
    color: white;
    border: none;
    border-radius: 50px;
    padding: 12px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(139, 69, 19, 0.3);
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    animation: pulse 2s infinite;
  }
  
  .pwa-install-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(139, 69, 19, 0.4);
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  
  .offline-indicator {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #f39c12;
    color: white;
    padding: 8px;
    text-align: center;
    font-size: 14px;
    z-index: 1001;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  
  .pwa-update-banner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #3498db;
    color: white;
    padding: 12px;
    z-index: 1002;
  }
  
  .update-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;
    max-width: 600px;
    margin: 0 auto;
  }
  
  .update-content button {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    padding: 6px 12px;
    cursor: pointer;
    font-size: 12px;
    transition: background 0.2s ease;
  }
  
  .update-content button:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  .pwa-notification {
    position: fixed;
    bottom: 80px;
    right: 20px;
    background: white;
    color: #333;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    z-index: 1003;
    max-width: 350px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    animation: slideInRight 0.3s ease;
  }
  
  .pwa-notification.success {
    border-left: 4px solid #27ae60;
  }
  
  .pwa-notification.warning {
    border-left: 4px solid #f39c12;
  }
  
  .pwa-notification.info {
    border-left: 4px solid #3498db;
  }
  
  .pwa-notification.error {
    border-left: 4px solid #e74c3c;
  }
  
  .notification-content {
    flex: 1;
  }
  
  .notification-content strong {
    display: block;
    margin-bottom: 4px;
  }
  
  .notification-content p {
    margin: 0;
    font-size: 14px;
    opacity: 0.8;
  }
  
  .pwa-notification button {
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    opacity: 0.5;
    transition: opacity 0.2s ease;
  }
  
  .pwa-notification button:hover {
    opacity: 1;
  }
  
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @media (max-width: 480px) {
    .pwa-install-btn {
      bottom: 10px;
      right: 10px;
      padding: 10px 16px;
      font-size: 13px;
    }
    
    .pwa-notification {
      bottom: 70px;
      right: 10px;
      left: 10px;
      max-width: none;
    }
    
    .update-content {
      flex-direction: column;
      gap: 10px;
      text-align: center;
    }
  }
`;

// Inject PWA styles
const styleSheet = document.createElement('style');
styleSheet.textContent = pwaStyles;
document.head.appendChild(styleSheet);

// Initialize PWA Manager
window.pwaManager = new PWAManager();

export default PWAManager;